/**
 * @sunday/util: the small helpers every Sunday widget shares. One copy of each, nowhere else.
 *
 *   esc(s)              text → safe HTML text (& < > " ')
 *   money(cents)        store currency, no paise: ₹25,999 (symbol and number style from the store,
 *                       printed once by snippets/sunday-head.liquid as data-sunday-money)
 *   emi(cents, months)  the monthly amount at 0%, rounded up to whole rupees (in cents)
 *   shade(colour, k)    a mattress colour made darker (k < 0) or lighter (k > 0), as CSS color-mix
 *   calm()              true when the visitor asked for reduced motion
 *   fill(text, vars)    '[name]' and '{name}' placeholders → values
 */

const CFG = (() => {
  try {
    return JSON.parse(document.querySelector('script[data-sunday-money]').textContent) || {};
  } catch (e) {
    return {};
  }
})();
const SYMBOL = CFG.symbol ?? '';
const NUM = (() => {
  try {
    return new Intl.NumberFormat(CFG.locale || 'en-IN', { maximumFractionDigits: 0 });
  } catch (e) {
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
  }
})();

const ENT = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ENT[c]);

export const money = (cents) => SYMBOL + NUM.format(Math.round((Number(cents) || 0) / 100));

export const emi = (cents, months) => Math.ceil((Number(cents) || 0) / 100 / Math.max(1, Number(months) || 1)) * 100;

/** k from -1 (towards black) to 1 (towards the card colour); 0 returns the colour. */
export function shade(colour, k = 0) {
  if (!k) return colour;
  const to = k < 0 ? 'var(--sd-art-black, #232019)' : 'var(--sd-card, #fff)';
  const keep = Math.round((1 - Math.min(1, Math.abs(k))) * 100);
  return `color-mix(in srgb, ${colour} ${keep}%, ${to})`;
}

/* 21 Oct (AR): delivery dates read "Tue 27/10": the weekday in the page's language (the browser names it, so no
   day names live in code), then day/month as two-digit numbers. Used by the delivery widget, Help me choose and the cart. */
const DLANG = (typeof document !== 'undefined' && document.documentElement.lang || 'en').toLowerCase();
const WDAY = new Intl.DateTimeFormat(DLANG.startsWith('en') ? 'en-US' : `${DLANG}-IN`, { weekday: 'short' });
const two = (n) => String(n).padStart(2, '0');
/** [weekday, 'dd/mm'] so a narrow box can drop the weekday. */
export const dayParts = (d) => [WDAY.format(d), `${two(d.getDate())}/${two(d.getMonth() + 1)}`];
/** 'Tue 27/10' */
export const dayStr = (d) => dayParts(d).filter(Boolean).join(' ');

const RM = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
export const calm = () => !!(RM && RM.matches);

export const fill = (text, vars = {}) =>
  String(text ?? '').replace(/\[(\w+)\]|\{(\w+)\}/g, (m, a, b) => {
    const k = a || b;
    return k in vars ? String(vars[k]) : m;
  });

/* A tiny tick in the hand (24 Oct, AR: Press to feel it, Turn to see inside). Android vibrates at the exact moment.
   iPhone Safari has no vibrate; toggling a switch-style checkbox gives its system tick, but only inside a tap and
   only on Safari builds with switch checkboxes (iOS 17.4 to 26.4; blocked in 26.5), so it is tried only there.
   Silent everywhere else. */
export const CAN_VIB = typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
export const CAN_SW = !CAN_VIB && typeof HTMLInputElement !== 'undefined' && 'switch' in HTMLInputElement.prototype;
let hapEl;
export function tick(ms = 10) {
  try {
    if (CAN_VIB) {
      navigator.vibrate(ms);
      return;
    }
    if (!CAN_SW) return;
    if (!hapEl) {
      hapEl = document.createElement('label');
      hapEl.setAttribute('aria-hidden', 'true');
      hapEl.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none';
      const i = document.createElement('input');
      i.type = 'checkbox';
      i.setAttribute('switch', '');
      i.tabIndex = -1;
      hapEl.appendChild(i);
      document.body.appendChild(hapEl);
    }
    hapEl.click();
  } catch (e) {}
}
